import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useState } from 'react';
import { Copy,} from 'lucide-react';
import type { Deal } from '../../services/dealPricing';

/**
 * COPY A DEAL INTO A VARIANT.
 *
 * The modal states plainly what carries over and what does not, because the
 * four resets are the surprising part: an operator who expects a copy of a live
 * landing-page deal to be live on the landing page needs to be told here, not
 * to discover it when nobody signs up.
 *
 * Leaving the code blank lets the server suffix the source's (`X` -> `X-2`).
 * Typing one means it is used exactly or the copy is refused — a code an
 * operator typed is a code they are about to give to a partner.
 */
const DealCopyModal: React.FC<{
  deal: Deal;
  onCancel: () => void;
  onConfirm: (data: { name?: string; code?: string }) => Promise<void>;
}> = ({ deal, onCancel, onConfirm }) => {
  const [name, setName] = useState(`${deal.name} — Copy`);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm({
        name: name.trim() || undefined,
        code: code.trim() ? code.trim().toUpperCase() : undefined,
      });
    } catch (e: any) {
      setError(
        e?.message === 'code_taken'
          ? 'That code is already in use by another deal.'
          : (e?.message || 'Could not copy the deal'),
      );
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-5 text-stone-800 shadow-2xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-full bg-rose-100 p-2"><Copy className="h-[18px] w-[18px] text-rose-600" /></div>
          <h2 className="text-[15px] font-bold text-stone-800">Copy “{deal.name}”</h2>
        </div>

        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-stone-500">Name</label>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mb-3 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-rose-400"
        />

        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-stone-500">Code</label>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={`Leave blank for ${(deal.code || '').toUpperCase()}-2`}
          className="mb-1 w-full rounded-lg border border-stone-200 px-3 py-2 font-mono text-sm outline-none focus:border-rose-400"
        />
        <p className="mb-3 text-[10.5px] text-stone-400">
          A code you type is used exactly as typed, or the copy is refused — never silently changed.
        </p>

        <div className="space-y-1.5 rounded-xl border border-stone-200 bg-stone-50 p-3 text-[11.5px] text-stone-600">
          <div className="font-semibold text-stone-700">The copy carries everything</div>
          <div>Pricing · packages · device kit &amp; finance terms · documents · commission plan · tags</div>
          <div className="mt-1.5 border-t border-stone-200 pt-1.5 font-semibold text-stone-700">Reset on the copy</div>
          <div className="flex flex-wrap gap-1.5">
            {['Inactive', 'Not on landing page', 'Not default', 'Uses 0'].map((t) => (
              <span key={t} className="rounded-full bg-stone-200 px-2 py-[1.5px] text-[10px] font-bold text-stone-600">{t}</span>
            ))}
          </div>
        </div>

        <p className="mt-3 text-[10.5px] leading-snug text-stone-400">
          The copy is a fully independent deal, starting unversioned. It records that it came from
          “{deal.name}” so the tree can draw it — editing either one never changes the other.
        </p>

        {error && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700">
            {error}
          </p>
        )}

        <div className="mt-4 flex gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="min-h-[44px] flex-1 rounded-xl bg-stone-100 px-4 py-2.5 text-sm font-medium text-stone-700 transition hover:bg-stone-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="min-h-[44px] flex-1 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-50"
          >
            {busy ? <span className="flex justify-center"><RubyGemMark size={20} drawing color="currentColor" /></span> : 'Create copy'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealCopyModal;
